import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CurrencyIcon from '@mui/icons-material/CurrencyExchange';
import { ShippingPolicy, MinimumOrderType } from '../../../data/types/domain';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useFormContext } from '../context/FormContext';
import { currencies } from '../../../data/common';

type ShippingPolicyFormData = Omit<ShippingPolicy, "id" | "business_id" | "created_at" | "updated_at">;

interface ShippingPolicyStepProps {
  initialData?: ShippingPolicyFormData[];
}

const validationSchema = Yup.object().shape({
  shippingPolicies: Yup.array()
    .min(1, 'At least one shipping policy is required')
    .of(
      Yup.object().shape({
        from_country: Yup.number()
          .required('From country is required')
          .min(1, 'Please select a from country'),
        to_country: Yup.number()
          .required('To country is required')
          .min(1, 'Please select a to country'),
        minimum_order_type: Yup.string()
          .required('Minimum order type is required')
          .oneOf([MinimumOrderType.AMOUNT, MinimumOrderType.PER_KG], 'Invalid minimum order type'),
        minimum_order_amount: Yup.number()
          .required('Minimum order amount is required')
          .min(0, 'Minimum order amount must be greater than or equal to 0'),
        currency: Yup.string()
          .required('Currency is required')
          .min(1, 'Currency cannot be empty')
      })
    ),
});

export interface ShippingPolicyStepRef {
  isValid: () => Promise<boolean>;
  getErrors: () => Promise<string[]>;
  getData: () => Promise<any>;
}

export const ShippingPolicyStep = forwardRef<ShippingPolicyStepRef, ShippingPolicyStepProps>(({ initialData }, ref) => {
  const { countries, isLoading, getCountryName } = useFormContext();
  
  const initialPolicyState = {
    from_country: 0,
    to_country: 0,
    minimum_order_type: MinimumOrderType.AMOUNT,
    minimum_order_amount: 0,
    currency: 'GBP',
  };
  
  const [newPolicy, setNewPolicy] = useState(initialPolicyState);
  const [currencyOpen, setCurrencyOpen] = useState(false);

  const initialValues = {
    shippingPolicies: initialData || []
  };

  useImperativeHandle(ref, () => ({
    isValid: async () => {
      try {
        await validationSchema.validate({ shippingPolicies: formikRef.current?.values.shippingPolicies }, { abortEarly: false });
        return true;
      } catch (err) {
        return false;
      }
    },
    getErrors: async () => {
      try {
        await validationSchema.validate({ shippingPolicies: formikRef.current?.values.shippingPolicies }, { abortEarly: false });
        return [];
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          return err.errors;
        }
        return ['An unknown error occurred'];
      }
    },
    getData: async () => {
      // No need to add city fields - they're no longer in the database schema
      const policies = formikRef.current?.values.shippingPolicies || [];
      return policies;
    }
  }));

  // Set initial countries if available
  useEffect(() => {
    if (countries.length > 0 && !newPolicy.from_country && !newPolicy.to_country) {
      const defaultCountry = countries[0].id;
      setNewPolicy(prev => ({
        ...prev,
        from_country: defaultCountry,
        to_country: defaultCountry
      }));
    }
  }, [countries, newPolicy.from_country, newPolicy.to_country]);

  const handleTextChange = (field: keyof typeof newPolicy) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setNewPolicy(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectChange = (field: keyof typeof newPolicy) => (
    e: SelectChangeEvent<number | string>
  ) => {
    const value = field.includes('country') ? Number(e.target.value) : e.target.value;
    setNewPolicy(prev => ({ ...prev, [field]: value }));
  };

  const handleCurrencyChange = (event: React.SyntheticEvent, newValue: any) => {
    if (newValue) {
      setNewPolicy(prev => ({ ...prev, currency: newValue.code }));
    }
  };

  const formikRef = React.useRef<any>(null);

  const handleAddPolicy = () => {
    if (!formikRef.current) return;
    
    const updatedPolicies = [...(formikRef.current.values.shippingPolicies || []), newPolicy];
    formikRef.current.setFieldValue('shippingPolicies', updatedPolicies);
    formikRef.current.validateForm();
    
    // Reset to initial state but keep the last selected countries
    const lastFromCountry = newPolicy.from_country;
    const lastToCountry = newPolicy.to_country;
    setNewPolicy({
      ...initialPolicyState,
      from_country: lastFromCountry,
      to_country: lastToCountry
    });
  };

  const handleRemovePolicy = (index: number) => {
    if (!formikRef.current) return;
    
    const updatedPolicies = formikRef.current.values.shippingPolicies.filter((_: any, i: number) => i !== index);
    formikRef.current.setFieldValue('shippingPolicies', updatedPolicies);
    formikRef.current.validateForm();
  };

  // Find the currency object for the current code
  const selectedCurrency = currencies.find(c => c.code === newPolicy.currency) || currencies.find(c => c.code === 'USD');

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {({ values, errors, touched }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Shipping Policies
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>From Country</InputLabel>
                  <Select
                    value={newPolicy.from_country || ''}
                    onChange={handleSelectChange('from_country')}
                    label="From Country"
                    disabled={isLoading.countries}
                  >
                    {countries.map(country => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {isLoading.countries && <CircularProgress size={20} sx={{ position: 'absolute', right: 10, top: 10 }} />}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>To Country</InputLabel>
                  <Select
                    value={newPolicy.to_country || ''}
                    onChange={handleSelectChange('to_country')}
                    label="To Country"
                    disabled={isLoading.countries}
                  >
                    {countries.map(country => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {isLoading.countries && <CircularProgress size={20} sx={{ position: 'absolute', right: 10, top: 10 }} />}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Minimum Order Type</InputLabel>
                  <Select
                    value={newPolicy.minimum_order_type}
                    onChange={handleSelectChange('minimum_order_type')}
                    label="Minimum Order Type"
                  >
                    <MenuItem value={MinimumOrderType.AMOUNT}>Amount</MenuItem>
                    <MenuItem value={MinimumOrderType.PER_KG}>Per KG</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Minimum Order Amount"
                  type="number"
                  fullWidth
                  value={newPolicy.minimum_order_amount}
                  onChange={handleTextChange('minimum_order_amount')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  open={currencyOpen}
                  onOpen={() => setCurrencyOpen(true)}
                  onClose={() => setCurrencyOpen(false)}
                  options={currencies}
                  value={selectedCurrency}
                  getOptionLabel={(option) => `${option.flag} ${option.code} - ${option.name}`}
                  onChange={handleCurrencyChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <CurrencyIcon color="action" sx={{ mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" component="span" sx={{ mr: 1, fontSize: '1.2rem' }}>
                        {option.flag}
                      </Typography>
                      <Box>
                        <Typography variant="body1">
                          {option.code} {option.symbol && `(${option.symbol})`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              {touched.shippingPolicies && errors.shippingPolicies && values.shippingPolicies?.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {String(errors.shippingPolicies)}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleAddPolicy}
                  fullWidth
                  disabled={!newPolicy.from_country || !newPolicy.to_country}
                >
                  Add Shipping Policy
                </Button>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Added Shipping Policies
            </Typography>
            
            {!values.shippingPolicies || values.shippingPolicies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No shipping policies added yet. Add at least one shipping policy.
              </Typography>
            ) : (
              <List>
                {values.shippingPolicies.map((policy: ShippingPolicyFormData, index: number) => {
                  const currencyInfo = currencies.find(c => c.code === policy.currency);
                  return (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={`${getCountryName(policy.from_country)} → ${getCountryName(policy.to_country)}`}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>Min. Order: {policy.minimum_order_amount}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              {currencyInfo?.flag && <span style={{ marginRight: 2 }}>{currencyInfo.flag}</span>}
                              <span>{currencyInfo?.code || policy.currency}</span>
                            </span>
                            <span>({policy.minimum_order_type})</span>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemovePolicy(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Form>
      )}
    </Formik>
  );
}); 